import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { AxiosError } from "axios";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useSnackbar } from "notistack";

import SpinnerButton from "@packages/common/src/components/form/SpinnerButton";
import useDialog from "@packages/skillspace/src/common/components/dialog-provider/useDialog";

import RenderFormFields from "../common/RenderFormFields";
import { loginFormFields, validationSchema } from "./helpers";
import { neutral } from "../../../common/config/colors";
import { strObj } from "../accountsTypes";
import { getErrorMessage } from "../../../common/utils/error";
import { FRONTEND_DASHBOARD_URL } from "../../../common/utils/constants";
import { FRONTEND_ORGANIZER_DASHBOARD_URL } from "../../../organizer/utils/constants";

const SITE_KEY = "0x4AAAAAABiEWQSAJcU-KwJm"; // 🔁 Replace with your actual site key

const Login: React.FC<{ email?: string }> = ({ email }) => {
  const { displayDialog } = useDialog();
  const router = useRouter();
  const { enqueueSnackbar } = useSnackbar();

  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const [cfToken, setCfToken] = useState("");

  const initialState = {
    input: email || "",
    password: "",
  };

  loginFormFields[0].disabled = !!email;

  useEffect(() => {
    if (email) {
      enqueueSnackbar(`Please login to accept an invitation`, {
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "info",
        persist: true,
      });
    }
  }, [email, enqueueSnackbar]);

  useEffect(() => {
    const scriptId = "cf-turnstile-script";

    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if ((window as any).turnstile) {
          (window as any).turnstile.render("#cf-turnstile", {
            sitekey: SITE_KEY,
            callback: (token: string) => setCfToken(token),
          });
        }
      };
      document.body.appendChild(script);
    } else {
      if ((window as any).turnstile && turnstileRef.current) {
        (window as any).turnstile.render("#cf-turnstile", {
          sitekey: SITE_KEY,
          callback: (token: string) => setCfToken(token),
        });
      }
    }
  }, []);

  const handleSubmit = async (data: strObj) => {
    if (!cfToken) {
      enqueueSnackbar("Please complete the human verification.", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      return;
    }

    try {
      const isValidEmail = await Yup.string().email().isValid(data["input"]);

      let requestData: strObj = {
        password: data.password,
        "cf-turnstile-response": cfToken, // ✅ Include token
      };

      if (isValidEmail) {
        requestData["email"] = data["input"];
      } else {
        requestData["username"] = data["input"];
      }

      const accountsService = new (
        await import("../../service/accountsService")
      ).default();
      const response = await accountsService.login(requestData);

      if (router.query?.next) {
        window.location.href = router.query?.next as string;
      } else {
        if (response?.data?.has_dphi_business_organization) {
          window.location.href = FRONTEND_ORGANIZER_DASHBOARD_URL;
        } else {
          window.location.href = FRONTEND_DASHBOARD_URL;
        }
      }
    } catch (error) {
      displayDialog({
        title: "Error!",
        content: "",
        variant: "error",
        agreeText: "Retry",
        onAgree: (closeDialog: any) => closeDialog(),
        onDisagree: () => {},
        children: (
          <Typography variant="subtitle2">
            {getErrorMessage((error as AxiosError).response?.data)}
          </Typography>
        ),
      });
    }
  };

  return (
    <Formik
      initialValues={initialState}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Form>
          <Box pt={1}>
            <RenderFormFields fields={loginFormFields} />
            <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}>
              <Link href="/accounts/password/reset/" passHref>
                <Typography
                  variant="body2"
                  sx={{ cursor: "pointer", pt: 1, color: neutral[900] }}
                >
                  Forgot Password?
                </Typography>
              </Link>
            </Box>
          </Box>

          {/* Turnstile CAPTCHA */}
          <div
            id="cf-turnstile"
            ref={turnstileRef}
            style={{ marginTop: "24px" }}
          />

          <SpinnerButton type="submit" disabled={isSubmitting} sx={{ mt: 2 }}>
            Login
          </SpinnerButton>

          <Typography pt={6} variant="body2">
            Don&apos;t have an account?{" "}
            <Link href="/accounts/signup" passHref>
              <Typography
                variant="body2"
                component="strong"
                sx={{ fontWeight: 700, cursor: "pointer" }}
              >
                Sign Up
              </Typography>
            </Link>
          </Typography>
        </Form>
      )}
    </Formik>
  );
};

export default Login;
